"use client"

import Image from "next/image"
import { useRef } from "react"
import { motion, useReducedMotion, useScroll, useTransform, type Variants } from "framer-motion"

const facts = [
  ["Price", "$8.4M"],
  ["Beds", "6"],
  ["Baths", "7.5"],
  ["Interior", "8,400 sq ft"],
  ["Lot", "3.0 ac"],
  ["Built", "1924"],
]

const documents = ["Floor plan", "Disclosure", "Survey", "Septic", "Permit history"]

// Cinematic easing — slow expo-out, the "luxury" register.
const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1]

const textContainer: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.14, delayChildren: 0.35 } },
}

const textItem: Variants = {
  hidden: { opacity: 0, y: 24, filter: "blur(10px)" },
  visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.9, ease: EASE } },
}

const asideContainer: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.16, delayChildren: 0.5 } },
}

const asideItem: Variants = {
  hidden: { opacity: 0, y: 28, filter: "blur(8px)" },
  visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.8, ease: EASE } },
}

const factsContainer: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06, delayChildren: 0.2 } },
}

const factItem: Variants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
}

export function ListingDossierHero() {
  const reduce = useReducedMotion()
  const sectionRef = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  })
  // Image drifts slower than the scroll → cinematic depth behind the fold.
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "12%"])
  // Keep the scrim strong through the parallax so the overlaid copy stays readable.
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.85])

  return (
    <section ref={sectionRef} className="bg-[#eef1ea] text-[#172016]">
      <div className="mx-auto grid min-h-[calc(100svh-86px)] max-w-[1500px] gap-5 px-5 py-5 lg:grid-cols-[1fr_390px]">
        {/* HERO IMAGE FRAME */}
        <div className="relative min-h-[680px] overflow-hidden">
          {/* parallax layer */}
          <motion.div
            style={{ y: reduce ? 0 : imageY }}
            className="absolute inset-0 -top-[8%] h-[120%]"
          >
            {/* develop-in reveal */}
            <motion.div
              initial={{ opacity: 0, scale: 1.07, filter: "blur(14px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              transition={{ duration: 1.5, ease: EASE }}
              className="absolute inset-0"
            >
              {/* slow Ken-Burns breathing */}
              <motion.div
                animate={reduce ? undefined : { scale: [1, 1.06] }}
                transition={reduce ? undefined : { duration: 22, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
                className="absolute inset-0"
              >
                <Image
                  src="https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=2400&q=92"
                  alt="1924 Tudor revival estate in Greenwich"
                  fill
                  priority
                  className="object-cover"
                  sizes="(min-width: 1024px) 72vw, 100vw"
                />
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-gradient-to-t from-[#172016] via-[#172016]/85 via-50% to-[#172016]/0 to-[78%]" />

          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.2 }}
            className="absolute left-5 top-5 bg-white px-4 py-3 text-xs font-black uppercase tracking-[0.18em]"
          >
            MLS #170582414
          </motion.div>

          <div className="absolute bottom-5 left-5 right-5 grid gap-5 text-white lg:grid-cols-[1fr_auto] lg:items-end">
            <motion.div variants={textContainer} initial="hidden" animate="visible" className="[text-shadow:0_2px_14px_rgba(7,12,9,0.4)]">
              <motion.p variants={textItem} className="text-sm font-black uppercase tracking-[0.22em] text-emerald-100">Round Hill · Greenwich</motion.p>
              <motion.h1 variants={textItem} className="mt-3 max-w-4xl text-5xl font-semibold leading-[0.9] tracking-normal sm:text-7xl">
                18 Briar Hollow Lane
              </motion.h1>
              <motion.p variants={textItem} className="mt-4 max-w-2xl text-lg leading-8 text-white/90">
                A 1924 Tudor revival on three private acres, with original millwork,
                a Workstead kitchen, restored gardens, and a guest cottage.
              </motion.p>
            </motion.div>
            <motion.div variants={textItem} initial="hidden" animate="visible">
              <a href="#tour" className="group inline-block bg-white px-6 py-4 text-center text-sm font-black uppercase tracking-[0.16em] text-[#172016] transition-transform duration-300 hover:-translate-y-0.5">
                Book private tour
              </a>
            </motion.div>
          </div>
        </div>

        {/* DOSSIER ASIDE */}
        <motion.aside variants={asideContainer} initial="hidden" animate="visible" className="grid gap-5">
          <motion.div variants={asideItem} className="border border-[#172016]/15 bg-white p-5">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-emerald-800">Listing dossier</p>
            <motion.div variants={factsContainer} className="mt-5 grid grid-cols-2 gap-px overflow-hidden bg-[#172016]/12">
              {facts.map(([label, value]) => (
                <motion.div key={label} variants={factItem} className="bg-white p-4">
                  <div className="text-[11px] uppercase tracking-[0.16em] text-[#60705b]">{label}</div>
                  <div className="mt-2 text-2xl font-black">{value}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div variants={asideItem} className="relative min-h-[260px] overflow-hidden border border-[#172016]/15 bg-[#172016] p-5 text-white">
            <div className="absolute inset-5 border border-dashed border-emerald-100/35" />
            <div className="absolute left-[18%] top-[24%] h-[30%] w-[38%] border-2 border-emerald-100/80 bg-emerald-100/10" />
            <div className="absolute bottom-[22%] right-[18%] h-[30%] w-[30%] border-2 border-amber-100/80 bg-amber-100/10" />
            <div className="relative">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-emerald-100">Floor plan packet</p>
              <h2 className="mt-3 text-3xl font-semibold">Room-by-room tour before the showing.</h2>
            </div>
          </motion.div>

          <motion.div variants={asideItem} className="border border-[#172016]/15 bg-white p-5">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-emerald-800">Request documents</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {documents.map((doc) => (
                <span key={doc} className="border border-[#172016]/15 px-3 py-2 text-sm font-semibold">{doc}</span>
              ))}
            </div>
          </motion.div>
        </motion.aside>
      </div>
    </section>
  )
}
